import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import type { SearchData } from '@eti/providers';
import { useDateFormat, useSearch, useTranslation } from '@eti/providers';
import type { SearchLocation, SelfServiceRebookingTraveler } from '@eti/schema-types';
import { TripType } from '@eti/schema-types';
import type { MutableRefObject } from 'react';
import { NUMBER_OF_STOPS } from '../../constants/searchFiltersConstants';
import { getFormattedSearchData } from '../../utils/search';
import {
  CABIN_CLASS,
  CARRIER_CODES,
  DEPARTURE_DATE,
  DEPARTURE_TIME_OF_DAY,
  DESTINATION,
  MULTI_BOUND,
  ORIGIN,
  passengerFields,
  PASSENGERS_FORM_SECTION,
  RETURN_DATE,
  RETURN_TIME_OF_DAY,
  SINGLE_BOUND,
  stopoverFields,
  TRIP_TYPE,
} from '../../widgets/search-form/constants/formFields';
import { formatGetDate } from '../../widgets/search-form/utils/format';
import {
  resolveSearchUrl,
  type SearchRedirectConfig,
  useSearchRedirectConfig,
} from '../../widgets/search-form/utils/searchRedirect';
import SearchFormNew from '../../widgets/search-form/components/SearchFormNew';

export type FormBound = {
  origin?: SearchLocation | null;
  destination?: SearchLocation | null;
  departureDate: Date | string;
  returnDate: Date | string;
  departureTimeOfDay?: string;
  returnTimeOfDay?: string;
};

export type FormPassengers = {
  adults: number;
  children: number;
  childrenAges: number[];
  infants: number;
  infantsAges: number[];
};

export interface SearchFormValues {
  singleBound: FormBound;
  multiBound: FormBound[];
  cabinClass: string;
  directFlight: boolean;
  passengers: FormPassengers;
  tripType?: TripType;
  stopoverEnabled: boolean;
  shortStopover: boolean;
  oneDayStopover: boolean;
  oneNightStopover: boolean;
  twoNightsStopover: boolean;
  carrierCodes: string[];
}

export type RebookingData = {
  criteria: (string | null)[] | undefined;
  initialValues: {
    passengers: FormPassengers;
  };
  formConstraints: { passengers: { maxNumberOfTravelers: number; isDisabled: boolean } };
  travelers: (SelfServiceRebookingTraveler | null)[];
  validatingCarriers: (string | null)[] | undefined;
} | null;

type SearchFormNewContainerProps = {
  onSearch: (keepFilters: boolean, searchData: SearchData) => void;
  rebookingData?: RebookingData;
  search?: SearchData;
  setValueRef?: MutableRefObject<UseFormSetValue<SearchFormValues>>;
  shouldShowRebookingBanner?: boolean;
  searchFormWatchRef?: MutableRefObject<UseFormWatch<SearchFormValues>>;
};

type TravelerTextsType = {
  adult: string;
  adults: string;
  child: string;
  children: string;
  infant: string;
  infants: string;
};

const handleOnSubmit =
  (
    onSearch: (keepFilters: boolean, searchData: SearchData) => void,
    travelerTexts: TravelerTextsType,
    searchRedirectConfig: SearchRedirectConfig | null,
    currentSearchData?: SearchData,
    setSearchData?: (searchData: SearchData) => void,
    departureErrorMessage?: string,
    returnErrorMessage?: string,
  ) =>
  async (values: SearchFormValues) => {
    if (departureErrorMessage) {
      return;
    }
    if (returnErrorMessage) {
      return;
    }
    if (searchRedirectConfig) {
      const redirectUrl = await resolveSearchUrl(searchRedirectConfig, values);

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }

    const selectedStopovers = {
      ...(values.shortStopover && { [stopoverFields.SHORT]: values.shortStopover }),
      ...(values.oneDayStopover && { [stopoverFields.ONE_DAY]: values.oneDayStopover }),
      ...(values.oneNightStopover && { [stopoverFields.ONE_NIGHT]: values.oneNightStopover }),
      ...(values.twoNightsStopover && { [stopoverFields.TWO_NIGHTS]: values.twoNightsStopover }),
    };

    const searchVariables = {
      ...getFormattedSearchData(values, travelerTexts),
      filters: {
        ...currentSearchData?.filters,
        ...(values.directFlight ? { [NUMBER_OF_STOPS]: '0' } : { [NUMBER_OF_STOPS]: null }),
        ...(values.stopoverEnabled
          ? {
              [stopoverFields.TYPES]: Object.keys(selectedStopovers),
            }
          : {}),
      },
    };

    if (setSearchData) {
      setSearchData(searchVariables);
    }

    sessionStorage.removeItem('hideTripReminder');
    sessionStorage.removeItem('selectedTripSelectionKey');
    onSearch(true, searchVariables);
  };

const SearchFormNewContainer = ({
  onSearch,
  rebookingData,
  search,
  searchFormWatchRef,
  setValueRef,
  shouldShowRebookingBanner,
}: SearchFormNewContainerProps) => {
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const searchRedirectConfig = useSearchRedirectConfig();
  const travelerTexts = {
    adult: t('Adult'),
    adults: t('Adults'),
    child: t('Child'),
    children: t('Children'),
    infant: t('Infant'),
    infants: t('Infants'),
  };
  const { date } = useDateFormat();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sevenDaysAfter = new Date(today);
  sevenDaysAfter.setDate(today.getDate() + 7);

  const methods = useForm<SearchFormValues>({
    defaultValues: search || {
      [SINGLE_BOUND]: {
        [ORIGIN]: null,
        [DESTINATION]: null,
        [DEPARTURE_DATE]: formatGetDate(date)(tomorrow),
        [RETURN_DATE]: formatGetDate(date)(sevenDaysAfter),
        [DEPARTURE_TIME_OF_DAY]: '',
        [RETURN_TIME_OF_DAY]: '',
      },
      [MULTI_BOUND]: [
        {
          [ORIGIN]: null,
          [DESTINATION]: null,
          [DEPARTURE_DATE]: formatGetDate(date)(tomorrow),
        },
        {
          [ORIGIN]: null,
          [DESTINATION]: null,
          [DEPARTURE_DATE]: '',
        },
      ],
      [CABIN_CLASS]: 'ECONOMY',
      [CARRIER_CODES]: [],
      [PASSENGERS_FORM_SECTION]: {
        [passengerFields.ADULTS]:
          rebookingData && rebookingData.initialValues.passengers.adults
            ? rebookingData.initialValues.passengers.adults
            : 1,
        [passengerFields.CHILDREN]:
          rebookingData && rebookingData.initialValues.passengers.children
            ? rebookingData.initialValues.passengers.children
            : 0,
        [passengerFields.CHILDREN_AGES]:
          rebookingData && rebookingData.initialValues.passengers.childrenAges
            ? rebookingData.initialValues.passengers.childrenAges
            : [7, 7, 7, 7, 7, 7, 7, 7, 7],
        [passengerFields.INFANTS]:
          rebookingData && rebookingData.initialValues.passengers.infants
            ? rebookingData.initialValues.passengers.infants
            : 0,
        [passengerFields.INFANTS_AGES]:
          rebookingData && rebookingData.initialValues.passengers.infantsAges
            ? rebookingData.initialValues.passengers.infantsAges
            : [1, 1, 1, 1, 1, 1, 1, 1, 1],
      },
      [TRIP_TYPE]: TripType.Return,
      [stopoverFields.ENABLED]: false,
      [stopoverFields.SHORT]: false,
      [stopoverFields.ONE_DAY]: false,
      [stopoverFields.ONE_NIGHT]: false,
      [stopoverFields.TWO_NIGHTS]: false,
    },
    mode: 'onBlur',
  });

  if (setValueRef) {
    // eslint-disable-next-line no-param-reassign
    setValueRef.current = methods.setValue;
  }

  if (searchFormWatchRef) {
    // eslint-disable-next-line no-param-reassign
    searchFormWatchRef.current = methods.watch;
  }

  return (
    <FormProvider {...methods}>
      <form
        aria-label={t('ScreenReader.SearchForm.FormLabel')}
        onSubmit={methods.handleSubmit(
          handleOnSubmit(
            onSearch,
            travelerTexts,
            searchRedirectConfig,
            currentSearchData,
            setSearchData,
          ),
        )}
      >
        <SearchFormNew
          rebookingData={rebookingData}
          shouldShowRebookingBanner={shouldShowRebookingBanner}
        />
        <DevTool control={methods.control} />
      </form>
    </FormProvider>
  );
};

export default SearchFormNewContainer;
